import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Send, MessageSquare, User, Image, X, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

import { API_URL } from '../utils/api';

const MessagesPage = () => {
  const { conversationId } = useParams();
  const [searchParams] = useSearchParams();
  const targetUserId = searchParams.get('user');
  const navigate = useNavigate();
  const { user, loading: authLoading, api, token } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [startingNewChat, setStartingNewChat] = useState(false);
  const [targetUserInfo, setTargetUserInfo] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (authLoading) return;
    
    if (!user) {
      navigate('/login');
      return;
    }
    fetchConversations();
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [user, authLoading]);

  // Handle ?user= query parameter to start conversation with specific user
  useEffect(() => {
    if (targetUserId && user && !loading) {
      handleTargetUser();
    }
  }, [targetUserId, conversations, user, loading]);

  const handleTargetUser = async () => {
    // Check if we already have a conversation with this user
    const existingConv = conversations.find(c => 
      c.participants.includes(targetUserId)
    );

    if (existingConv) {
      // Open existing conversation
      selectConversation(existingConv);
    } else {
      // Need to start a new conversation - fetch user info first
      setStartingNewChat(true);
      try {
        const res = await fetch(`${API_URL}/api/users/${targetUserId}/public`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const userData = await res.json();
          setTargetUserInfo(userData);
        } else {
          console.error('Failed to fetch user info:', res.status);
          toast.error('Could not find user');
        }
      } catch (err) {
        console.error('Failed to fetch target user info', err);
        toast.error('Failed to load user info');
      }
    }
  };

  const startNewConversation = async () => {
    if (!targetUserId || !newMessage.trim()) return;
    
    setSending(true);
    try {
      // Create new conversation
      const res = await api().post('/conversations', {
        recipient_id: targetUserId,
        message: newMessage.trim()
      });
      
      setNewMessage('');
      setStartingNewChat(false);
      setTargetUserInfo(null);
      
      // Navigate to the new conversation
      if (res.data.conversation_id) {
        navigate(`/messages/${res.data.conversation_id}`, { replace: true });
        fetchConversations();
      }
    } catch (err) {
      console.error('Failed to start conversation', err);
      toast.error(err.response?.data?.detail || 'Failed to start conversation');
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    if (conversationId) {
      fetchMessages(conversationId);
      // Start polling for new messages
      pollIntervalRef.current = setInterval(() => {
        fetchMessages(conversationId, true);
      }, 5000);
    }
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [conversationId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async () => {
    try {
      const res = await api().get('/conversations');
      setConversations(res.data);
      
      // If URL has conversation ID, find it
      if (conversationId) {
        const conv = res.data.find(c => c.id === conversationId);
        if (conv) {
          setSelectedConversation(conv);
        }
      }
    } catch (err) {
      console.error('Failed to fetch conversations', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (convId, silent = false) => {
    try {
      const res = await api().get(`/conversations/${convId}/messages`);
      setMessages(res.data);
      
      // Update unread count in conversations
      setConversations(prev => prev.map(c => 
        c.id === convId ? { ...c, unread_count: 0 } : c
      ));
    } catch (err) {
      if (!silent) {
        console.error('Failed to fetch messages', err);
      }
    }
  };

  const selectConversation = (conv) => {
    setSelectedConversation(conv);
    navigate(`/messages/${conv.id}`);
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Check file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image too large. Max 10MB allowed.');
      return;
    }

    setSelectedImage(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const removeSelectedImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const res = await fetch(`${API_URL}/api/upload/chat-image`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });

    if (!res.ok) {
      throw new Error('Failed to upload image');
    }

    const data = await res.json();
    return data.url;
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if ((!newMessage.trim() && !selectedImage) || !conversationId) return;

    setSending(true);
    setUploadingImage(!!selectedImage);

    try {
      let imageUrl = null;

      // Upload image if selected
      if (selectedImage) {
        try {
          imageUrl = await uploadImage(selectedImage);
        } catch (err) {
          console.error('Failed to upload image', err);
          toast.error('Failed to upload image');
          setSending(false);
          setUploadingImage(false);
          return;
        }
      }

      await api().post(`/conversations/${conversationId}/messages`, {
        content: newMessage.trim() || '',
        image_url: imageUrl
      });
      
      setNewMessage('');
      removeSelectedImage();
      fetchMessages(conversationId);
      fetchConversations();
    } catch (err) {
      console.error('Failed to send message', err);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
      setUploadingImage(false);
    }
  };

  const getOtherParticipant = (conv) => {
    if (!conv || !user) return { name: 'Unknown', id: null };
    const otherId = conv.participants.find(p => p !== user.id);
    return {
      name: conv.participant_names?.[otherId] || 'Unknown',
      id: otherId
    };
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / 86400000);

    if (days === 0) {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    } else if (days === 1) {
      return 'Yesterday';
    } else if (days < 7) {
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-stone-300 border-t-teal-600 rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-bold text-stone-900 mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
          Messages
        </h1>

        <div className="bg-white border border-stone-200 rounded-sm overflow-hidden" style={{ height: 'calc(100vh - 200px)' }}>
          <div className="flex h-full">
            {/* Conversations List */}
            <div className={`w-full md:w-80 border-r border-stone-200 flex flex-col ${
              conversationId || startingNewChat ? 'hidden md:flex' : 'flex'
            }`}>
              <div className="p-4 border-b border-stone-200">
                <h2 className="font-semibold text-stone-900">Conversations</h2>
              </div>
              
              <ScrollArea className="flex-1">
                {conversations.length === 0 ? (
                  <div className="p-8 text-center">
                    <MessageSquare className="h-12 w-12 text-stone-300 mx-auto mb-4" />
                    <p className="text-stone-500">No conversations yet</p>
                    <p className="text-sm text-stone-400 mt-1">
                      Contact a seller to start chatting
                    </p>
                  </div>
                ) : (
                  conversations.map((conv) => {
                    const other = getOtherParticipant(conv);
                    return (
                      <button
                        key={conv.id}
                        onClick={() => selectConversation(conv)}
                        className={`w-full p-4 text-left border-b border-stone-100 hover:bg-stone-50 transition-colors ${
                          conversationId === conv.id ? 'bg-teal-50' : ''
                        }`}
                        data-testid={`conversation-${conv.id}`}
                      >
                        <div className="flex gap-3">
                          <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                            <User className="h-5 w-5 text-teal-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <p className="font-medium text-stone-900 truncate">{other.name}</p>
                              {conv.last_message_at && (
                                <span className="text-xs text-stone-400">
                                  {formatTime(conv.last_message_at)}
                                </span>
                              )}
                            </div>
                            {conv.listing_title && (
                              <p className="text-xs text-teal-600 truncate mt-0.5">
                                Re: {conv.listing_title}
                              </p>
                            )}
                            {conv.last_message && (
                              <p className="text-sm text-stone-500 truncate mt-0.5">
                                {conv.last_message}
                              </p>
                            )}
                          </div>
                          {conv.unread_count > 0 && (
                            <div className="w-5 h-5 rounded-full bg-gradient-to-r from-sky-500 to-teal-500 text-white text-xs flex items-center justify-center flex-shrink-0">
                              {conv.unread_count}
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })
                )}
              </ScrollArea>
            </div>

            {/* Messages Area */}
            <div className={`flex-1 flex flex-col ${
              !conversationId && !startingNewChat ? 'hidden md:flex' : 'flex'
            }`}>
              {conversationId && selectedConversation ? (
                <>
                  {/* Chat Header */}
                  <div className="p-4 border-b border-stone-200 flex items-center gap-3">
                    <button
                      onClick={() => navigate('/messages')}
                      className="md:hidden p-1 hover:bg-stone-100 rounded"
                    >
                      <ArrowLeft className="h-5 w-5" />
                    </button>
                    <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                      <User className="h-5 w-5 text-teal-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-stone-900">
                        {getOtherParticipant(selectedConversation).name}
                      </p>
                      {selectedConversation.listing_title && (
                        <Link
                          to={`/listing/${selectedConversation.listing_id}`}
                          className="text-xs text-teal-600 hover:underline"
                        >
                          {selectedConversation.listing_title}
                        </Link>
                      )}
                    </div>
                  </div>

                  {/* Messages */}
                  <ScrollArea className="flex-1 p-4">
                    <div className="space-y-4">
                      {messages.map((msg, idx) => (
                        <motion.div
                          key={msg.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className={`flex ${msg.sender_id === user.id ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`message-bubble ${
                            msg.sender_id === user.id ? 'message-sent' : 'message-received'
                          }`}>
                            {msg.image_url && (
                              <a 
                                href={msg.image_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="block mb-2"
                              >
                                <img 
                                  src={msg.image_url} 
                                  alt="Shared image" 
                                  className="max-w-[200px] max-h-[200px] rounded-lg object-cover cursor-pointer hover:opacity-90 transition-opacity"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              </a>
                            )}
                            {msg.content && (
                              <p className="text-sm">{msg.content}</p>
                            )}
                            <p className={`text-xs mt-1 ${
                              msg.sender_id === user.id ? 'text-sky-200' : 'text-stone-400'
                            }`}>
                              {formatTime(msg.created_at)}
                            </p>
                          </div>
                        </motion.div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>
                  </ScrollArea>

                  {/* Message Input */}
                  <form onSubmit={sendMessage} className="p-4 border-t border-stone-200">
                    {/* Image Preview */}
                    {imagePreview && (
                      <div className="mb-3 relative inline-block">
                        <img 
                          src={imagePreview} 
                          alt="Preview" 
                          className="h-20 w-20 object-cover rounded-lg border border-stone-200"
                        />
                        <button
                          type="button"
                          onClick={removeSelectedImage}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                    
                    <div className="flex gap-2">
                      {/* Hidden file input */}
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleImageSelect}
                        accept="image/*"
                        className="hidden"
                      />
                      
                      {/* Image upload button */}
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={sending}
                        className="px-3"
                        title="Attach image"
                      >
                        <Image className="h-4 w-4 text-stone-500" />
                      </Button>
                      
                      <Input
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder={selectedImage ? "Add a caption (optional)..." : "Type a message..."}
                        className="flex-1"
                        data-testid="message-input"
                      />
                      <Button
                        type="submit"
                        disabled={sending || (!newMessage.trim() && !selectedImage)}
                        className="bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white"
                        data-testid="send-message-btn"
                      >
                        {sending ? (
                          uploadingImage ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                          )
                        ) : (
                          <Send className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </form>
                </>
              ) : startingNewChat && targetUserInfo ? (
                // New conversation with specific user (from admin panel)
                <>
                  {/* Chat Header */}
                  <div className="p-4 border-b border-stone-200 flex items-center gap-3">
                    <button
                      onClick={() => {
                        setStartingNewChat(false);
                        setTargetUserInfo(null);
                        navigate('/messages', { replace: true });
                      }}
                      className="p-1 hover:bg-stone-100 rounded"
                    >
                      <ArrowLeft className="h-5 w-5" />
                    </button>
                    <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                      <User className="h-5 w-5 text-teal-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-stone-900">
                        {targetUserInfo.name}
                      </p>
                      <p className="text-xs text-stone-500">
                        New conversation
                      </p>
                    </div>
                  </div>

                  {/* New conversation prompt */}
                  <div className="flex-1 flex items-center justify-center p-8">
                    <div className="text-center">
                      <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-4">
                        <User className="h-8 w-8 text-teal-600" />
                      </div>
                      <h3 className="text-lg font-semibold text-stone-900 mb-2">
                        Start a conversation with {targetUserInfo.name}
                      </h3>
                      <p className="text-stone-500 text-sm">
                        Type a message below to start the conversation
                      </p>
                    </div>
                  </div>

                  {/* Message Input */}
                  <form onSubmit={(e) => { e.preventDefault(); startNewConversation(); }} className="p-4 border-t border-stone-200">
                    <div className="flex gap-2">
                      <Input
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type your first message..."
                        className="flex-1"
                        autoFocus
                      />
                      <Button
                        type="submit"
                        disabled={sending || !newMessage.trim()}
                        className="bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white"
                      >
                        {sending ? (
                          <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                        ) : (
                          <Send className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </form>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-center p-8">
                  <div>
                    <MessageSquare className="h-16 w-16 text-stone-200 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-stone-900 mb-2">
                      Select a conversation
                    </h3>
                    <p className="text-stone-500">
                      Choose a conversation from the list to start messaging
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessagesPage;
